import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Heart } from 'lucide-react';

export default function YourPresence() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Your Presence is the Most Precious Gift</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 8</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Main Quote Card */}
          <div className="bg-gradient-to-br from-rose-900/20 to-pink-900/20 border border-rose-700/30 rounded-2xl p-12 mb-12 text-center">
            <Heart className="h-16 w-16 text-rose-300 mx-auto mb-8" />
            <blockquote className="text-4xl md:text-5xl font-serif text-white leading-relaxed mb-8">
              "Your presence is the most precious gift you can give to another human being."
            </blockquote>
            <p className="text-rose-200 text-xl">~ Marshall Rosenberg</p>
          </div>

          {/* Image Description */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8 mb-12">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center">
                <span className="text-3xl">🌸</span>
              </div>
              <div className="flex-1">
                <p className="text-slate-300 italic leading-relaxed">
                  Visual: Two hands, one offering a bouquet of wildflowers to another, with a natural, grassy background—
                  symbolizing the gift of presence and connection in nature.
                </p>
              </div>
            </div>
          </div>

          {/* Second Quote Card */}
          <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-700/30 rounded-2xl p-12 text-center">
            <blockquote className="text-3xl md:text-4xl font-serif text-white leading-relaxed mb-8">
              "The most important human need is to contribute to life, our own and others. 
              That is, to make life more wonderful."
            </blockquote>
            <p className="text-blue-200 text-xl">- Marshall Rosenberg</p>
          </div>

          {/* Reflection Section */}
          <div className="mt-12 bg-slate-800/40 border border-slate-700/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Reflection</h2>
            <div className="space-y-4 text-slate-300">
              <p className="leading-relaxed">
                Being fully present with another person is one of the most profound gifts we can offer. 
                It means setting aside our own thoughts, judgments, and agendas to truly be with them in this moment.
              </p>
              <p className="leading-relaxed">
                When we show up with genuine presence, we create a space where connection can flourish—
                a space where both giving and receiving become acts of making life more wonderful.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
